import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    createOrUpdateBorrower,
    getBorrower,
    getTermsAndConditionsAgreement,
} from '@/apiCalls/borrower';
import { trackLogin } from '@/apiCalls/uniqueIDTransferHelper';
import {
    EVerificationResponseStatus,
    IVerifyCodeReturn,
    sendEmailCode,
    sendSmsCode,
    verifyEmailCode,
    verifySmsCode,
} from '@/apiCalls/verification';
import { Button, EButtonType } from '@/components/Button';
import { Input } from '@/components/Input';
import {
    EEmploymentStatus,
    EIncomeSource,
    ELoanUsage,
    EUserFlow,
    EVehicleAssetType,
    selectAddressByIndex,
    selectEmail,
    selectFirstName,
    selectLoanUsage,
    selectMobile,
    selectVehicle,
    setIsVehicleRegistrationKnown,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectAgreedToTermsAndConditions,
    selectDisplay,
    selectIsAssetKnownViaParams,
    selectModal,
    selectPartnerCountry,
    selectSignupOptions,
    selectSmsLastRequestTimestamp,
    selectSmsRequestAttempts,
    selectUserFlow,
    setDisplay,
    setModal,
    setSmsLastRequestTimestamp,
    setSmsRequestAttempts,
} from '@/context/display';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import {
    EUserLoginOption,
    selectMobileOrEmail,
    setIsLoggedIn,
} from '@/context/user';
import { Email } from '@/UserInput/Email';
import { REACT_APP_ENV } from '@/utils/constants';
import { isEmail } from '@/utils/isEmail';
import { isMobile } from '@/utils/isMobile';
import { EConversionType, reportConversion } from '@/utils/reportConversion';

import { FirstName } from '../FirstName';
import { PostCode } from '../PostCode';
import { TermsAndConditions } from '../TermsAndConditions';

export interface IProps {
    baseId: string;
    showErrors: boolean;
    disabled?: boolean;
}

export const testId = 'LoginCode';

// Todo: Separate the login code and the login button into separate components
export function LoginCode({
    baseId,
    showErrors,
    disabled = false,
}: IProps): JSX.Element {
    // ***** Redux *****
    const currentState = useAppSelector(selectEmail);
    const dispatch = useAppDispatch();
    const loginMethod = useAppSelector(selectMobileOrEmail);
    const mobileNumber = useAppSelector(selectMobile);
    const email = useAppSelector(selectEmail);
    const loanUsage = useAppSelector(selectLoanUsage);
    const display = useAppSelector(selectDisplay);
    const modal = useAppSelector(selectModal);
    const firstName = useAppSelector(selectFirstName);
    const signupOptions = useAppSelector(selectSignupOptions);
    const vehicle = useAppSelector(selectVehicle);
    const isAssetKnownFromParams = useAppSelector(selectIsAssetKnownViaParams);
    const country = useAppSelector(selectPartnerCountry);
    const termsAndConditions = useAppSelector(selectAgreedToTermsAndConditions);
    const isProduction =
        REACT_APP_ENV === 'production' || REACT_APP_ENV === 'uat';
    const loggingAttempts = isProduction ? 5 : 100; // Allow more attempts in non-production environments
    // ***** Local State *****
    const [code, setCode] = useState<string>('');
    const [invalidCode, setInvalidCode] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [sent, setSent] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [rateLimitingError, setRateLimitingError] = useState<boolean>(false);
    const userFlow = useAppSelector(selectUserFlow);
    const noLender =
        userFlow === EUserFlow.NO_LENDER ||
        userFlow === EUserFlow.NO_LENDER_EARLY_SIGNUP;
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { postcode } = address;

    const smsRequestAttempts = useAppSelector(selectSmsRequestAttempts);
    const smsLastRequestTimestamp = useAppSelector(
        selectSmsLastRequestTimestamp
    );
    const customerEmail = useSelector((state: RootState) => selectEmail(state));

    useEffect(() => {
        if (code?.length !== 6) return;
        // todo: dispatch check code
        checkLoginCode();
    }, [code]);

    useEffect(() => {
        // Validate timestamp is a proper Date object when loaded from storage
        if (
            smsLastRequestTimestamp &&
            !(smsLastRequestTimestamp instanceof Date)
        ) {
            // Convert string timestamp to Date if needed
            const timestamp = new Date(smsLastRequestTimestamp);
            if (!isNaN(timestamp.getTime())) {
                dispatch(setSmsLastRequestTimestamp(timestamp));
            } else {
                dispatch(setSmsLastRequestTimestamp(null));
            }
        }
    }, []);

    useEffect(() => {
        if (
            loginMethod === EUserLoginOption.MOBILE &&
            smsRequestAttempts >= loggingAttempts &&
            smsLastRequestTimestamp
        ) {
            const now = Date.now();
            const timeSinceLastRequest =
                now - (smsLastRequestTimestamp?.getTime() || 0);

            if (timeSinceLastRequest < 15 * 60 * 1000) {
                setRateLimitingError(true);
            } else {
                setRateLimitingError(false);
            }
        } else {
            setRateLimitingError(false);
        }
    }, [loginMethod, smsRequestAttempts, smsLastRequestTimestamp]);

    // ***** Event Handlers *****
    function handleChange(value: string | boolean): void {
        setInvalidCode(false);
        setError(false);
        if (typeof value === 'boolean') return;
        if (value === currentState) return;
        if (value?.length > 6) return;
        setCode(value);
    }

    async function handleClick(): Promise<void> {
        // Check rate limiting before sending
        if (
            loginMethod === EUserLoginOption.MOBILE &&
            smsRequestAttempts >= loggingAttempts
        ) {
            const now = Date.now();
            const timeSinceLastRequest =
                now - (smsLastRequestTimestamp?.getTime() || 0);

            if (timeSinceLastRequest < 15 * 60 * 1000) {
                // 15 minutes
                setRateLimitingError(true);
                dispatch(setSmsRequestAttempts(0));

                return; // Don't proceed with sending
            } else {
                // Reset if 15 minutes have passed
                dispatch(setSmsRequestAttempts(0));
                setRateLimitingError(false);
                dispatch(setSmsLastRequestTimestamp(null));
            }
        }

        let code: EVerificationResponseStatus | void = undefined;

        // Only update attempts and timestamp if we're actually sending
        dispatch(setSmsRequestAttempts(smsRequestAttempts + 1));
        dispatch(setSmsLastRequestTimestamp(new Date()));

        if (loginMethod === EUserLoginOption.MOBILE) {
            code = await loginViaMobile();
        } else {
            code = await loginViaEmail();
        }
        useCode(code);
    }

    async function login(created?: boolean): Promise<void> {
        if (smsRequestAttempts > loggingAttempts) {
            dispatch(setSmsRequestAttempts(0));
        }
        dispatch(setIsLoggedIn(true));

        // 1) hydrate store with everything the server has
        await getBorrower();

        // Records which screen they were on when they logged in. The login
        // control sits in the corner of every screen, so a cluster on one
        // screen means applicants are leaving to resume later rather than
        // finishing — and that screen is the reason.
        //
        // signedUp vs loggedIn: the server's created flag settles a brand-new
        // borrower (with the Register modal / not-found branch as fallback).
        // An EXISTING borrower only counts as returning when they have
        // history with THIS partner — getBorrower has just hydrated the
        // application for the current partner, so an empty application means
        // they are a first-timer here, however long the borrower has existed
        // elsewhere. Emitted after hydration for exactly that reason; the
        // modal is still open, so loginFromScreen is unaffected.
        const newBorrower = created ?? (modal === EModals.REGISTER || notFound);
        const application = store.getState().borrowerReducer.application;
        const hasHistoryHere = Boolean(
            application.applicationId || application.id
        );
        trackLogin(!newBorrower && hasHistoryHere ? 'loggedIn' : 'signedUp');

        // 2) now createOrUpdate will carry the real income/expense/etc
        await createOrUpdateBorrower();
        // Fired, not awaited. It returns the agreement and dispatches
        // nothing, and the callback that used to wrap everything below took
        // no argument — so the whole post-login branch, and with it closing
        // the modal, waited on a round-trip whose result was discarded. That
        // was a fourth sequential request between entering the code and the
        // screen changing.
        void getTermsAndConditionsAgreement().catch(() => undefined);

        // Save/create the borrower

        const state = store.getState();

        // Registering is the last thing asked of someone with no income, so
        // this is where they are told. Outside the novated block below and
        // not conditional on the lender count: no income fails every loan
        // type, whether or not a quote has been run.
        if (
            display === EDisplays.INCOME &&
            state.borrowerReducer.employments?.employments[0]?.incomeSource ===
                EIncomeSource.NO_INCOME
        ) {
            dispatch(setModal(null));
            dispatch(setDisplay(EDisplays.NOINCOMEINELIGIBLE));
            return;
        }
        const isNovatedLease =
            state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;
        if (
            isNovatedLease &&
            (display === EDisplays.VEHICLE ||
                display === EDisplays.BASIC ||
                display === EDisplays.INCOME)
        ) {
            if (state.lenderReducer.lenders?.length === 0) {
                if (
                    display === EDisplays.VEHICLE ||
                    display === EDisplays.BASIC
                ) {
                    dispatch(setModal(null));
                    dispatch(setDisplay(EDisplays.INELIGIBLE));
                    return;
                }
                const employmentStatus =
                    state.borrowerReducer.employments?.employments[0]
                        .status;
                if (
                    employmentStatus === EEmploymentStatus.CASUAL &&
                    loanUsage === ELoanUsage.NOVATED
                ) {
                    dispatch(setModal(null));
                    dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                    return;
                } else if (
                    state.lenderReducer.lenders?.length === 0 &&
                    loanUsage === ELoanUsage.NOVATED
                ) {
                    dispatch(setModal(null));
                    dispatch(setDisplay(EDisplays.INCOMEINELIGIBLE));
                    return;
                }
            }
        }
        if (noLender && isAssetKnownFromParams) {
            dispatch(setModal(null));
            if (loanUsage === ELoanUsage.BUSINESS) {
                dispatch(setDisplay(EDisplays.BUSINESS));
                return;
            } else {
                dispatch(setDisplay(EDisplays.BASIC));
                return;
            }
        }

        // T&C is now handled during login/registration via TermsAndConditions component
        // No need to show EXPRESS_PRIVACY modal

        if (!state.borrowerReducer.application.isSubmitted) {
            dispatch(setModal(null));
        }

        if (display === EDisplays.SERVICING_SUCCESS) {
            dispatch(setDisplay(EDisplays.IDENTIFICATION));
        }

        // Don't keep user on QUICK_CALCULATOR for Early Sign-up flow
        // Let the navigation logic below handle it
        if (
            display === EDisplays.QUICK_CALCULATOR &&
            !(
                userFlow === EUserFlow.EARLY_SIGNUP ||
                userFlow === EUserFlow.EARLY_SIGNUP.toLocaleUpperCase() ||
                userFlow === EUserFlow.INSTANT_LEAD_CAPTURE ||
                userFlow ===
                    EUserFlow.INSTANT_LEAD_CAPTURE.toLocaleUpperCase()
            )
        ) {
            dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
        }

        // commercial applications
        if (
            loanUsage === ELoanUsage.BUSINESS &&
            display === EDisplays.BASIC
        ) {
            dispatch(setDisplay(EDisplays.IDENTIFICATION));
        }

        if (display === EDisplays.CAMPAIGN_LANDING) {
            if (loanUsage === ELoanUsage.BUSINESS) {
                dispatch(setDisplay(EDisplays.BUSINESS));
            } else {
                dispatch(setDisplay(EDisplays.BASIC));
            }
        }

        if (display === EDisplays.NO_LENDER_HOME) {
            if (!vehicle.isAssetTypeKnownViaParams && vehicle.isVehicleKnown) {
                dispatch(setDisplay(EDisplays.VEHICLE));
            } else {
                if (loanUsage === ELoanUsage.BUSINESS) {
                    dispatch(setDisplay(EDisplays.BUSINESS));
                } else {
                    dispatch(setDisplay(EDisplays.BASIC));
                }
            }
        }

        // Handle early signup flow navigation from QUICK_CALCULATOR
        if (
            display === EDisplays.QUICK_CALCULATOR &&
            (userFlow === EUserFlow.EARLY_SIGNUP ||
                userFlow === EUserFlow.EARLY_SIGNUP.toLocaleUpperCase() ||
                userFlow === EUserFlow.INSTANT_LEAD_CAPTURE ||
                userFlow === EUserFlow.INSTANT_LEAD_CAPTURE.toLocaleUpperCase())
        ) {
            // Close modal first
            dispatch(setModal(null));
            // Navigate to next screen using same logic as QuickCalculatorButton
            const state = store.getState();
            const isNovatedLease =
                state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;
            const isAssetKnownViaParams =
                state.displayReducer.isAssetKnownViaParams;
            const isVehicleNew =
                state.borrowerReducer.vehicle.condition === 'NEW';
            const storeSelectorOptions =
                state.displayReducer.partner.storeSelectorOptions;
            const partnerOverwriteId =
                state.borrowerReducer.application.partnerOverwriteId;
            const isAssetTypePersonalLoan =
                state.borrowerReducer.vehicle.assetType ===
                EVehicleAssetType.PERSONAL_LOAN;

            if (isAssetTypePersonalLoan) {
                dispatch(setDisplay(EDisplays.BASIC));
                return;
            }
            if (storeSelectorOptions && !partnerOverwriteId) {
                dispatch(setDisplay(EDisplays.STORE_SELECTOR));
            } else {
                if (isNovatedLease && isAssetKnownViaParams) {
                    dispatch(setDisplay(EDisplays.BASIC));
                    return;
                }
                if (isNovatedLease && !isAssetKnownViaParams) {
                    if (isVehicleNew) {
                        dispatch(setIsVehicleRegistrationKnown(false));
                    } else {
                        dispatch(setIsVehicleRegistrationKnown(true));
                    }
                    dispatch(setDisplay(EDisplays.VEHICLE));
                    return;
                }
                if (
                    display === EDisplays.QUICK_CALCULATOR &&
                    userFlow === EUserFlow.INSTANT_LEAD_CAPTURE
                ) {
                    dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
                    return;
                }
                dispatch(setDisplay(EDisplays.LOAN));
            }
            return;
        }

        // Tracking
        reportConversion(EConversionType.SUBMIT_LOAN_REQUEST);
    }

    // ***** Helpers ******
    async function loginViaMobile(): Promise<EVerificationResponseStatus | void> {
        if (!isMobile(mobileNumber)) return;
        if (mobileNumber === null) return;
        if (mobileNumber === undefined) return;
        setSending(true);

        const response = await sendSmsCode(mobileNumber, notFound);

        setSending(false);
        setSent(true);
        setRateLimitingError(false);
        return response;
    }

    async function loginViaEmail(): Promise<EVerificationResponseStatus | void> {
        if (!isEmail(email)) return;
        if (email === null) return;
        if (email === undefined) return;
        setSending(true);

        const response = await sendEmailCode(email);
        setSending(false);
        setSent(true);

        return response;
    }

    // Same functionality for both loginViaMobile and loginViaEmail
    function useCode(code: EVerificationResponseStatus | void): void {
        if (code === EVerificationResponseStatus.SUCCESSFUL) {
            setSent(true);
            setRateLimitingError(false);
            return;
        }

        if (code === EVerificationResponseStatus.INCORRECT_VERIFICATION_CODE) {
            setInvalidCode(true);
            return;
        }

        if (!code) {
            setNotFound(true);
            setSent(false);
            setSending(false);
            return;
        }

        setError(true);
        return;
    }

    async function checkLoginCode(): Promise<void> {
        if (code?.length !== 6) return;

        setChecking(true);
        try {
            let response: void | IVerifyCodeReturn = undefined;
            if (loginMethod === EUserLoginOption.MOBILE) {
                response = await checkLoginViaMobile();
            } else {
                response = await checkLoginViaEmail();
            }

            await useVerificationCode(response);
        } catch (error) {
            /*
             * Without this, a request that threw anywhere after the code was
             * accepted left the customer signed in, the spinner gone and the
             * modal still open, with nothing said. setError only covers an
             * unsuccessful verification status, never a rejected request — so
             * a 413 from createOrUpdateBorrower, or any outage in the two
             * calls login() awaits, simply stopped the journey in silence.
             */
            console.error('Login failed after verification:', error);
            setError(true);
        } finally {
            // Cleared here rather than when the verify request returns. It
            // used to drop the moment the code was accepted, leaving the two
            // slowest calls — hydrating the borrower and saving it — running
            // behind a screen that had stopped saying anything.
            setChecking(false);
        }
    }

    // Same functionality for both checkLoginViaMobile and checkLoginViaEmail
    async function useVerificationCode(
        response: IVerifyCodeReturn | void
    ): Promise<void> {
        if (response?.status === EVerificationResponseStatus.SUCCESSFUL) {
            await login(response.created);
            return;
        }

        if (
            response?.status ===
            EVerificationResponseStatus.INCORRECT_VERIFICATION_CODE
        ) {
            setInvalidCode(true);
            return;
        }

        setError(true);
        return;
    }

    async function checkLoginViaEmail(): Promise<IVerifyCodeReturn | void> {
        if (!isEmail(email)) return;
        if (email === null) return;
        if (email === undefined) return;

        const response = await verifyEmailCode(email, code);

        return response;
    }

    async function checkLoginViaMobile(): Promise<IVerifyCodeReturn | void> {
        if (!isMobile(mobileNumber)) return;
        if (mobileNumber === null) return;
        if (mobileNumber === undefined) return;

        const response = await verifySmsCode(mobileNumber, code);
        return response;
    }

    function isReadyToSendCode(): boolean {
        if (disabled || sending || sent) return false;

        if (loginMethod === EUserLoginOption.MOBILE) {
            if (!mobileNumber || !isMobile(mobileNumber)) return false;

            if (
                smsRequestAttempts >= loggingAttempts &&
                smsLastRequestTimestamp
            ) {
                const now = Date.now();
                const timeSinceLastRequest =
                    now - (smsLastRequestTimestamp?.getTime() || 0);

                if (timeSinceLastRequest < 15 * 60 * 1000) {
                    return false; // Button will be disabled
                }
            }

            if (notFound) {
                if (firstName === undefined || firstName.trim() === '')
                    return false;

                // Check T&C agreement for new users in Login modal
                if (modal === EModals.LOGIN) {
                    if (!termsAndConditions.isDefaultPrivacyAgreed?.agreed) {
                        return false;
                    }
                }

                if (signupOptions?.customerEmail) {
                    if (!customerEmail || !isEmail(customerEmail)) return false;
                }
                if (signupOptions?.zipcode) {
                    if (!postcode) return false;
                    return postcode.length === 4;
                }
            }
            return true;
        }

        if (loginMethod === EUserLoginOption.EMAIL) {
            return isEmail(email);
        }

        return false;
    }

    // ***** Render *****
    return (
        <div data-testid={testId} className='_flex flex-col'>
            {notFound && modal === EModals.LOGIN && (
                <>
                    <FirstName baseId={baseId} showErrors={false} />{' '}
                    {signupOptions?.zipcode && (
                        <PostCode baseId={baseId} showErrors={false} />
                    )}
                    {signupOptions?.customerEmail && (
                        <Email baseId={baseId} showErrors={false} />
                    )}
                    <TermsAndConditions baseId={baseId} />
                </>
            )}

            <div className='_flex space-x-2'>
                <div className='py-2 w-full'>
                    <div className='_flex'>
                        <Button
                            id={`${baseId}-button-sendCode`}
                            onClick={handleClick}
                            type={
                                isReadyToSendCode()
                                    ? EButtonType.PRIMARY
                                    : EButtonType.PRIMARY_DISABLED
                            }
                        >
                            <p> {sent ? 'Code Sent' : 'Send Code'}</p>
                        </Button>
                    </div>
                </div>
                <Input
                    placeholder='XXXXXX'
                    showErrors={showErrors && !code}
                    onChange={handleChange}
                    id={`${baseId}-input-verificationCode`}
                    value={code}
                    disabled={checking || (!sent && !disabled)}
                    showNumpad={true}
                />
            </div>

            {invalidCode && (
                <p className='text-red-500 text-center'>Invalid Code</p>
            )}
            {sending && <p className=' text-center'>Sending...</p>}
            {rateLimitingError && (
                <p className='text-red-500 text-center'>
                    Too many attempts, please try again later
                </p>
            )}
            {error && (
                <p className='text-red-500 text-center'>
                    Error, please try again
                </p>
            )}
            {notFound && modal === EModals.LOGIN && (
                <>
                    <p className='text-red-500 text-center text-sm pb-2'>
                        Sorry, you don&apos;t have an account with us
                    </p>
                </>
            )}
            {!notFound && (
                <p className='font-bold text-xs text-textBody text-center py-2'>
                    {modal === EModals.LOGIN ? "Don't" : 'Already'} have an
                    account?
                </p>
            )}
            {checking && (
                <p
                    className='_flex items-center justify-center gap-2 pt-1 text-sm text-textBody'
                    role='status'
                >
                    {/* Sized inline rather than the Loading component, which
                        is w-full h-full to fill a container and so took the
                        whole row and wrapped the words. */}
                    <span
                        aria-hidden='true'
                        className='h-4 w-4 shrink-0 rounded-full border-2 border-optionTrack border-t-primary motion-safe:animate-spin'
                    />
                    Checking your code…
                </p>
            )}
        </div>
    );
}
